'use client';

import { useId, useState } from 'react';
import { BlockRoot, PortalLink, tx, useCart, useCommerceSource, type CommerceSource } from '@akademi/site-builder-runtime';
import { useSolarText } from '../locales/useSolarText';
import { ActionLink, type LinkAction } from '../home/parts';
import { matchesCode, parseQuickOrder, type QuickOrderLine } from '../home/quickOrder';
import { tradeSavingsText, useTradeSavings } from '../home/tradeSavings';
import { TradeStyles } from '../styles/TradeStyles';
import { HOME_CSS } from '../styles/homeCss';

type Localized = string | Record<string, string>;

export interface SolarTradeBandProps {
  /** The band's eyebrow ("For the trade"), in Horizon's accent text voice. */
  kicker?: Localized;
  title?: Localized;
  text?: Localized;
  /**
   * The fact list under the copy — one amber-dot line per fact. Only what the shop can
   * keep belongs here: each line must stay inside a recorded assurance basis.
   */
  facts?: Localized[];
  account?: LinkAction;
  quickOrderTitle?: Localized;
  quickOrderPlaceholder?: Localized;
  /** Anchor id, so a hero's "Quick order" can link straight to the box. */
  anchor?: string;
  basketHref?: string;
}

type Outcome =
  | { code: string; quantity: number; kind: 'added'; title: string }
  | { code: string; quantity: number; kind: 'missing' | 'outOfStock' | 'failed' };

/**
 * The trade band, in Horizon's full-bleed ink-band anatomy
 * (`solar/docs/design/redesign-2026-10-01/`, `index.html` `.trade-band`): why to open an
 * account — eyebrow, heading, the amber-dot fact list — with the quick-order pad as a raised
 * panel beside it. The band paints edge to edge; the copy keeps the page column.
 *
 * <p>Quick order takes part numbers, one per line with an optional quantity, and puts each
 * EXACT match in the basket. Search is fuzzy by design, so every hit is checked against the
 * code the buyer typed — our product code or the manufacturer's part number — before anything
 * is added; a near miss is reported as not found rather than substituted. Each line reports
 * what happened to it.
 *
 * <p>The trade saving ("Trade accounts save typically X%, up to Y%") is read from the shop's
 * trade application requirements, where the backend computes it from the live RETAIL and
 * TRADE prices. It appears only when the backend sends one; no percentage is ever authored
 * or assumed here.
 */
export function SolarTradeBand({
  kicker,
  title,
  text,
  facts = [],
  account,
  quickOrderTitle,
  quickOrderPlaceholder,
  anchor = 'quick-order',
  basketHref = '/cart',
}: SolarTradeBandProps) {
  const t = useSolarText();
  const commerce = useCommerceSource();
  const cart = useCart();
  const fieldId = useId();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [invalid, setInvalid] = useState<string[]>([]);
  const savings = useTradeSavings();
  const canOrder = !!commerce && !!cart?.available;
  const shownFacts = facts.map((fact) => tx(fact)).filter(Boolean);

  async function submit() {
    if (!commerce || !cart?.available || busy) return;
    const parsed = parseQuickOrder(value);
    setInvalid(parsed.invalid);
    if (!parsed.lines.length) {
      setOutcomes([]);
      return;
    }
    setBusy(true);
    const results: Outcome[] = [];
    // One after the other: the basket takes one product per call, and parallel adds race the
    // basket token on a first add.
    for (const line of parsed.lines) {
      results.push(await orderLine(commerce, line, (productId) =>
        cart.add({ productId, quantity: String(line.quantity) }),
      ));
      setOutcomes([...results]);
    }
    setBusy(false);
  }

  return (
    <BlockRoot as="section" className="ae ae-trade" id={anchor || undefined}>
      <TradeStyles id="home" css={HOME_CSS} />
      <div className="ae-wrap ae-trade__grid">
        <div>
          {tx(kicker) ? <p className="ae-eyebrow">{tx(kicker)}</p> : null}
          {tx(title) ? <h2 className="ae-trade__title">{tx(title)}</h2> : null}
          {tx(text) ? <p className="ae-trade__text">{tx(text)}</p> : null}
          {shownFacts.length ? (
            <ul className="ae-trade__facts">
              {shownFacts.map((fact, index) => (
                <li key={index}>{fact}</li>
              ))}
            </ul>
          ) : null}
          {savings ? <p className="ae-trade__text ae-trade__saving">{tradeSavingsText(savings, t)}</p> : null}
          <ActionLink action={account} tone="ghost" />
        </div>
        {canOrder ? (
          <div className="ae-quick">
            <label htmlFor={fieldId}>{tx(quickOrderTitle) || t('quick.title')}</label>
            <div className="ae-quick__row">
              <textarea
                id={fieldId}
                value={value}
                spellCheck={false}
                autoCapitalize="characters"
                placeholder={tx(quickOrderPlaceholder)}
                onChange={(event) => setValue(event.target.value)}
              />
            </div>
            <button type="button" className="ae-btn ae-btn--ghost" disabled={busy || !value.trim()} onClick={submit}>
              {busy ? t('home.adding') : t('quick.submit')}
            </button>
            <p className="ae-quick__help">{t('quick.help')}</p>
            {outcomes.length || invalid.length ? (
              <ul className="ae-quick__results" role="status" aria-live="polite">
                {outcomes.map((outcome) => (
                  <li key={outcome.code}>
                    <code>{outcome.code}</code>
                    <span>× {outcome.quantity}</span>
                    {outcome.kind === 'added' ? (
                      <span className="ae-quick__ok">{t('quick.added', { title: outcome.title })}</span>
                    ) : (
                      <span className="ae-quick__bad">{t(`quick.${outcome.kind}`)}</span>
                    )}
                  </li>
                ))}
                {invalid.map((line) => (
                  <li key={`invalid:${line}`}>
                    <code>{line}</code>
                    <span className="ae-quick__bad">{t('quick.unreadable')}</span>
                  </li>
                ))}
                {outcomes.some((outcome) => outcome.kind === 'added') ? (
                  <li>
                    <PortalLink href={basketHref}>{t('home.viewBasket')}</PortalLink>
                  </li>
                ) : null}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </BlockRoot>
  );
}

/** How many search hits are checked for an exact match before a code counts as not found. */
const CANDIDATES = 5;

async function orderLine(
  commerce: CommerceSource,
  line: QuickOrderLine,
  add: (productId: number) => Promise<boolean>,
): Promise<Outcome> {
  const { code, quantity } = line;
  try {
    const listing = await commerce.listProducts({ q: code, size: CANDIDATES });
    for (const card of listing.items.slice(0, CANDIDATES)) {
      // Listing cards carry `sku` at runtime though the type does not declare it; the
      // manufacturer's part number is only on the detail read.
      let match = matchesCode(card as { sku?: string }, code);
      if (!match) {
        const detail = await commerce.getProductBySlug(card.slug).catch(() => null);
        match = !!detail && matchesCode(detail, code);
      }
      if (!match) continue;
      if (card.availability === 'OUT_OF_STOCK') return { code, quantity, kind: 'outOfStock' };
      return (await add(card.id))
        ? { code, quantity, kind: 'added', title: card.title }
        : { code, quantity, kind: 'failed' };
    }
    return { code, quantity, kind: 'missing' };
  } catch {
    return { code, quantity, kind: 'failed' };
  }
}
